/**
 * The account rules, one hook for the account piece and the studio header
 * (web-studio-sites D15), so sign-in stays one rule: Sign in with Telegram on
 * the page the visitor is on; signed in, the tab's live unread count and the
 * messages panel, and the account menu opening Bookings, Messages and Profile
 * (web-account-pages D3, D9). Draws nothing.
 */
import { useEffect, useMemo, useState } from 'react';

import { clientName, useSignOut, useTelegramSignIn, useWebSession } from '../../api/session';
import { usePieceEmit, usePieceOptions, type PieceEventHandler, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useMessagesPanel } from '../../messages/panelContext';
import { useWebChat } from '../../messages/WebChat';
import { usePageMover } from '../../pageMover';
import { contract, type AccountActions, type AccountEvents, type AccountState } from './contract';

export type AccountRuntime = PieceRuntime<typeof contract, AccountState, AccountActions>;

/** `E`: a host whose own events include the account's (the studio header), with the same payloads. */
export interface AccountInput<E extends AccountEvents = AccountEvents> extends PieceInput<E> {
  /** False when the host shows no Sign in (the studio header's `showSignIn`): Telegram's sign-in is not prepared. */
  readonly signIn?: boolean;
}

export function useAccount<E extends AccountEvents = AccountEvents>(input: AccountInput<E> = {}): AccountRuntime {
  const options = usePieceOptions(contract, input.options);
  // The host's handler takes every account event under the same name and payload (`E extends AccountEvents`).
  const emit = usePieceEmit(input.onEvent as PieceEventHandler<AccountEvents> | undefined);
  const navigate = usePageMover();
  const session = useWebSession();
  const signedIn = session.status === 'signedIn';
  const telegram = useTelegramSignIn(input.signIn ?? true);
  const signOut = useSignOut();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const { state: chat } = useWebChat();
  const panel = useMessagesPanel();
  const client = signedIn && session.user ? clientName(session.user) : null;
  // The message banner shows only on a page with this header on it (card 4 walk, Gabriel 2026-11-01).
  const { showHeader } = panel;
  useEffect(() => showHeader(), [showHeader]);

  const actions = useMemo<AccountActions>(
    () => ({
      signIn: () => {
        emit('signInPressed', {});
        telegram.signInOneTap();
      },
      toggleMenu: (open) => {
        setSignOutFailed(false);
        setMenuOpen((was) => open ?? !was);
      },
      toggleMessages: () => {
        setMenuOpen(false);
        if (panel.open) panel.closeMessages();
        else panel.openMessages();
        emit('messagesToggled', { open: !panel.open });
      },
      openAccount: (page) => {
        setMenuOpen(false);
        // The messages close as the page moves, never left open over Bookings or Profile (card 4 walk, Gabriel 2026-11-01).
        if (panel.open) panel.closeMessages();
        emit('accountOpened', { page });
        navigate(page === 'bookings' ? '/account/bookings' : '/account/profile');
      },
      // Signed out on screen only once the server ended the session (web-sign-in-checkout security review).
      signOut: () => {
        setSignOutFailed(false);
        void signOut().then((ended) => {
          if (!ended) {
            setSignOutFailed(true);
            return;
          }
          setMenuOpen(false);
          emit('signedOut', {});
        });
      },
    }),
    [emit, navigate, telegram, signOut, panel],
  );

  return {
    state: {
      session: session.status,
      client,
      signInReady: telegram.oneTapReady,
      signIn: telegram.state,
      signOutFailed,
      menuOpen: menuOpen && signedIn,
      unread: signedIn ? chat.unread : 0,
      messagesOpen: panel.open,
    },
    actions,
    options,
  };
}
